import type React from 'react';
import {StatChip} from '~/components/StatChip/StatChip';
import {COLORS} from '~/style/colors-ts';
import DollarCoinIcon from '~/icons/svg/DollarCoinIcon.svg?react';
import DbIcon from '~/icons/svg/DbIcon.svg?react';
import ChartIcon from '~/icons/svg/ChartIcon.svg?react';
import NoteIcon from '~/icons/svg/NoteIcon.svg?react';
import {findNode} from '../domain/exploreTree';
import {LOCALE} from '../domain/locale';
import {periodRange} from '../domain/period';
import type {Explore} from '../types/categoryProfileTypes';
import {KpiRow} from '~/components/KpiRow/KpiRow';

type Props = {data: Explore, selected: string, money: (v: number) => string};

/** KPI pills of Explore Categories (spec 3.3), for the selected scope or the whole workspace. */
export const ExploreKpis: React.FC<Props> = ({data, selected, money}) => {
    const kpis = data.kpis;
    if (!kpis) return null;
    const node = selected ? findNode(data.tree, selected) : undefined;
    const spend = node?.spend ?? kpis.totalSpend;
    const poSpend = node?.poSpend ?? kpis.poSpend;
    const poShare = node ? node.poSharePct : kpis.poSharePct;
    const leaves = node ? countLeaves(node.children) || 1 : kpis.leafCategories;
    // Every figure covers the Reporting period: the 12 months up to the latest month with data (not up to today), or
    // all data
    const months = data.period ? periodRange(data.period.start, data.period.end, LOCALE) : 'The reporting period';
    const which = data.period?.allData ? 'all data in the dataset' : 'the last 12 months with data (not counted back from today)';
    const during = data.period ? ` · ${months}` : '';
    return (
        <KpiRow>
            <StatChip icon={{element: <DollarCoinIcon/>, color: COLORS.success.main}} title="Total spend"
                      value={money(spend)}
                      tooltip={{type: 'info', text: `${months}: ${which} · Reporting period in the settings`}}/>
            <StatChip icon={{element: <ChartIcon/>, color: COLORS.secondary.main}} title="Categories"
                      value={leaves.toLocaleString(LOCALE)}
                      tooltip={{type: 'info', text: `Leaf categories · ${kpis.levels}-level taxonomy`}}/>
            <StatChip icon={{element: <DbIcon/>, color: COLORS.info.main}} title="Suppliers"
                      value={(node?.suppliers ?? kpis.suppliers).toLocaleString(LOCALE)}
                      tooltip={{type: 'info', text: `${node ? `Across ${node.name}` : 'Across all categories'}${during}`}}/>
            {kpis.showSpendUnderManagement && <StatChip icon={{element: <NoteIcon/>, color: COLORS.primary.main}} title="Spend under management"
                                                        value={poShare === null ? '—' : `${money(poSpend)} · ${poShare.toFixed(0)}%`}
                                                        tooltip={{type: 'info', text: `Spend on lines with a purchase order${during}`}}/>}
            {kpis.priceModelTargetPct !== undefined && kpis.showPriceModelSpend && (
                <StatChip icon={{element: <ChartIcon/>, color: COLORS.warning.main}} title="Spend on price models"
                          value={kpis.priceModelSharePct === null || kpis.priceModelSharePct === undefined ? '—'
                              : `${kpis.priceModelSharePct.toFixed(0)}%`}
                          tooltip={{type: 'info', text: `${kpis.priceModels ?? 0} active price model${kpis.priceModels === 1 ? '' : 's'}`
                              + ` · ${kpis.priceModelMode === 'category' ? 'whole spend of the modelled categories'
                                  : 'unit price × annual volume of each model, up to its category\u2019s spend'}`
                              + ` · target ${kpis.priceModelTargetPct}% of all spend${during}`}}/>
            )}
        </KpiRow>
    );
};

function countLeaves(nodes: Explore['tree']): number {
    return nodes.reduce((n, c) => n + (c.children.length ? countLeaves(c.children) : 1), 0);
}
